import React, { useMemo, useState } from 'react';
import Button from '../../../../../components/Button';
import {
  PromotionSchedule,
  PromotionScheduleHoliday,
  PromotionScheduleWeekMeta,
} from '../../../../../lib/api/promotionSchedule';
import { TRACK_ORDER } from '../../constants';
import { DAY_TYPE, DAY_TYPE_COLOR, WeekSegment, getWeekSegments, groupWeeksByMonth, isOverlapped } from '../../utils';
import { getTodayKst } from '../../utils/kst';
import { CalendarWeekRow } from './CalendarWeekRow';
import {
  StyledCalendarToolbar,
  StyledCalendarWrap,
  StyledEmpty,
  StyledMonthCard,
  StyledMonthTitle,
  StyledWeekdayCell,
  StyledWeekdayRow,
} from './styles';

type Props = {
  schedules: PromotionSchedule[];
  weeks: PromotionScheduleWeekMeta[];
  holidays: PromotionScheduleHoliday[];
  onClickSchedule: (schedule: PromotionSchedule) => void;
};

/** 월요일 시작 헤더. 토=파랑 / 일=빨강 (기존 구글시트 관례). */
const WEEKDAY_HEADER = [
  { label: '월', color: DAY_TYPE_COLOR[DAY_TYPE.WEEKDAY].headerText },
  { label: '화', color: DAY_TYPE_COLOR[DAY_TYPE.WEEKDAY].headerText },
  { label: '수', color: DAY_TYPE_COLOR[DAY_TYPE.WEEKDAY].headerText },
  { label: '목', color: DAY_TYPE_COLOR[DAY_TYPE.WEEKDAY].headerText },
  { label: '금', color: DAY_TYPE_COLOR[DAY_TYPE.WEEKDAY].headerText },
  { label: '토', color: DAY_TYPE_COLOR[DAY_TYPE.SATURDAY].headerText },
  { label: '일', color: DAY_TYPE_COLOR[DAY_TYPE.SUNDAY].headerText },
];

/**
 * 캘린더 뷰 — 같은 데이터를 월 단위로 재투영.
 *
 * 주의 소속 월은 서버가 준 `week.month`(폴라리스 규약)를 그대로 쓴다.
 * 그래서 월 격자의 첫/마지막 주에는 다른 달 날짜가 섞이는데, 흐리게 처리해 구분한다.
 * 주 경계를 넘는 항목은 주마다 잘려 그려지고, 잘린 쪽 모서리를 각지게 해서 연속임을 보여준다.
 */
export const CalendarView = (props: Props): React.JSX.Element => {
  const { schedules, weeks, holidays, onClickSchedule } = props;

  const today = getTodayKst();

  const holidayMap = useMemo(() => new Map(holidays.map((holiday) => [holiday.date, holiday.memo])), [holidays]);

  const holidayDates = useMemo(() => new Set(holidays.map((holiday) => holiday.date)), [holidays]);

  const monthGroups = useMemo(() => groupWeeksByMonth(weeks), [weeks]);

  /**
   * 접힌 월의 키 집합. 기본은 전부 펼침 —
   * 넓은 기간(연 단위)을 조회하면 월 카드가 12장까지 쌓이므로 필요한 달만 남기고 접을 수 있게 한다.
   */
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());
  /**
   * 여러 주에 걸친 항목은 막대가 주마다 잘려 별개 엘리먼트가 된다. CSS `:hover` 로 두면
   * 마우스가 올라간 주만 칠해져 "이번 주에 끝난다" 로 잘못 읽히므로 같은 id 조각 전부를 칠한다.
   *
   * ⚠️ **DOM 에 직접 class 를 붙이는 방식으로 가면 안 된다.** 막대는 antd `Tooltip` 의 자식이라
   * 툴팁이 열릴 때 antd 가 className 을 다시 쓰고, 그러면서 **hover 중인 막대에서만 강조가 지워진다**
   * (다른 주는 툴팁이 안 열려 그대로 남는다). 실제로 그 증상이 나서 상태로 되돌렸다.
   *
   * 리렌더 범위는 `CalendarWeekRow` 의 `React.memo` 가 줄인다 — 아래 `hoveredIdByWeek` 참조.
   */
  const [hoveredScheduleId, setHoveredScheduleId] = useState<number | null>(null);

  const monthKey = (year: number, month: number) => `${year}-${month}`;

  const toggleMonth = (key: string) =>
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  /**
   * 주별 레인 배치 — 주마다 전체 항목을 훑고 트랙별로 다시 걸러 정렬하므로 **한 번만** 계산한다.
   * 인라인으로 두면 렌더가 일어날 때마다 전 주를 다시 배치한다.
   */
  const laneRowsByWeek = useMemo(() => {
    const result = new Map<string, WeekSegment[][]>();

    monthGroups.forEach((monthGroup) => {
      monthGroup.weeks.forEach((week) => {
        // 트랙 순서를 주면 같은 트랙이 세로로 모인다 (캘린더 전용 배치)
        result.set(week.startDate, getWeekSegments(week, schedules, TRACK_ORDER));
      });
    });

    return result;
  }, [monthGroups, schedules]);

  /**
   * 주별 hover 대상 — 그 주에 hover 중인 항목이 **있을 때만** id, 없으면 null.
   *
   * 이렇게 해야 `CalendarWeekRow` 의 memo 가 실제로 걸린다. hover id 를 모든 주에 그대로
   * 내려주면 값이 매번 바뀌어 52주가 전부 다시 그려진다. 항목 하나는 보통 1~5주에 걸치므로
   * 다시 그려지는 주도 그만큼으로 줄어든다.
   */
  const hoveredIdByWeek = useMemo(() => {
    const result = new Map<string, number>();

    if (hoveredScheduleId === null) {
      return result;
    }

    laneRowsByWeek.forEach((laneRows, weekStartDate) => {
      const hasHovered = laneRows.some((laneRow) =>
        laneRow.some((segment) => segment.schedule.id === hoveredScheduleId),
      );

      if (hasHovered) {
        result.set(weekStartDate, hoveredScheduleId);
      }
    });

    return result;
  }, [laneRowsByWeek, hoveredScheduleId]);

  /** 접힌 헤더에 건수를 보여주기 위한 월별 항목 수 (기간이 그 달과 겹치는 항목). */
  const countByMonthKey = useMemo(() => {
    const counts = new Map<string, number>();

    monthGroups.forEach((monthGroup) => {
      const first = monthGroup.weeks[0];
      const last = monthGroup.weeks[monthGroup.weeks.length - 1];
      const count = schedules.filter((schedule) =>
        isOverlapped(schedule.startDate, schedule.endDate, first.startDate, last.endDate),
      ).length;
      counts.set(monthKey(monthGroup.year, monthGroup.month), count);
    });

    return counts;
  }, [monthGroups, schedules]);

  if (monthGroups.length === 0) {
    return <StyledEmpty>조회 기간을 선택해주세요.</StyledEmpty>;
  }

  /**
   * 조회 기간이 바뀌면 이전 구간의 월 키가 `collapsedMonths` 에 남는다.
   * 개수만 비교하면 그 잔여 키 때문에 실제로 펼쳐진 카드가 있는데도 전부 접힌 것으로 오판하고,
   * 버튼을 눌러도 펼쳐진 카드가 그대로 남는다 → **현재 월 키만** 보고 판정한다.
   */
  const monthKeys = monthGroups.map((group) => monthKey(group.year, group.month));
  const isAllCollapsed = monthKeys.every((key) => collapsedMonths.has(key));

  return (
    <StyledCalendarWrap>
      {monthGroups.length > 1 && (
        <StyledCalendarToolbar>
          <Button
            text={isAllCollapsed ? '전체 펼치기' : '전체 접기'}
            color="light"
            onClick={() => setCollapsedMonths(isAllCollapsed ? new Set() : new Set(monthKeys))}
          />
        </StyledCalendarToolbar>
      )}

      {monthGroups.map((monthGroup) => {
        const key = monthKey(monthGroup.year, monthGroup.month);
        const isCollapsed = collapsedMonths.has(key);

        return (
          <StyledMonthCard key={key}>
            <StyledMonthTitle type="button" $isCollapsed={isCollapsed} onClick={() => toggleMonth(key)}>
              <span className="collapse-caret">▼</span>
              {monthGroup.year}년 {monthGroup.month}월
              <span className="month-count">{countByMonthKey.get(key) ?? 0}건</span>
            </StyledMonthTitle>

            {!isCollapsed && (
              <>
                <StyledWeekdayRow>
                  {WEEKDAY_HEADER.map((weekday) => (
                    <StyledWeekdayCell key={weekday.label} $color={weekday.color}>
                      {weekday.label}
                    </StyledWeekdayCell>
                  ))}
                </StyledWeekdayRow>

                {monthGroup.weeks.map((week) => (
                  <CalendarWeekRow
                    key={week.startDate}
                    week={week}
                    laneRows={laneRowsByWeek.get(week.startDate) ?? []}
                    month={monthGroup.month}
                    today={today}
                    holidayMap={holidayMap}
                    holidayDates={holidayDates}
                    hoveredScheduleId={hoveredIdByWeek.get(week.startDate) ?? null}
                    onHoverChange={setHoveredScheduleId}
                    onClickSchedule={onClickSchedule}
                  />
                ))}
              </>
            )}
          </StyledMonthCard>
        );
      })}
    </StyledCalendarWrap>
  );
};
